import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, Newspaper, BookOpen, Users, LogOut, Plus, Pencil, Trash2, Eye, EyeOff, Video
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBooksList = () => {
  const { admin, token, logout } = useAuth();
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => { fetchBooks(); }, []);

  const fetchBooks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/books?status=all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setBooks(await response.json());
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/books/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setBooks(books.filter(b => b.id !== id));
        toast.success('Livre supprimé');
        setDeleteId(null);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const togglePublish = async (book) => {
    try {
      const newStatus = book.status === 'published' ? 'draft' : 'published';
      const response = await fetch(`${API_URL}/api/books/${book.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        const updated = await response.json();
        setBooks(books.map(b => b.id === book.id ? updated : b));
        toast.success(newStatus === 'published' ? 'Livre publié' : 'Livre dépublié');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleLogout = () => { logout(); navigate('/admin/login'); };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Tableau de bord', path: '/admin' },
    { icon: Newspaper, label: 'Actualités', path: '/admin/news' },
    { icon: BookOpen, label: 'Livres', path: '/admin/books', active: true },
    { icon: Video, label: 'Interviews', path: '/admin/videos' },
    { icon: Users, label: 'Utilisateurs', path: '/admin/users' }
  ];

  return (
    <div className="min-h-screen bg-stone-100 flex">
      <aside className="w-64 bg-stone-900 text-white flex flex-col">
        <div className="p-6 border-b border-stone-700">
          <h1 className="text-xl font-serif font-bold">Quid Novi</h1>
          <p className="text-stone-400 text-sm mt-1">Administration</p>
        </div>
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded transition-colors ${item.active ? 'bg-hermes-600 text-white' : 'text-stone-300 hover:bg-stone-800'}`}>
                  <item.icon className="w-5 h-5" /><span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-4 border-t border-stone-700">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 text-stone-400 hover:text-white hover:bg-stone-800 rounded transition-colors">
            <LogOut className="w-5 h-5" /><span>Déconnexion</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-serif font-bold text-stone-900">Catalogue des livres</h2>
            <p className="text-stone-600 mt-1">Gérez vos fiches livres</p>
          </div>
          <Link to="/admin/books/new" className="flex items-center gap-2 px-4 py-2 bg-hermes-600 text-white hover:bg-hermes-700 transition-colors">
            <Plus className="w-5 h-5" />Ajouter un livre
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {loading ? (
            <div className="col-span-full text-center py-12 text-stone-500">Chargement...</div>
          ) : books.length === 0 ? (
            <div className="col-span-full text-center py-12 text-stone-500">Aucun livre pour le moment</div>
          ) : (
            books.map((book) => (
              <div key={book.id} className="bg-white shadow-sm overflow-hidden group">
                <div className="aspect-[2/3] bg-stone-200 relative">
                  {book.cover_image_url ? (
                    <img src={book.cover_image_url} alt={book.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><BookOpen className="w-12 h-12 text-stone-400" /></div>
                  )}
                  {book.status !== 'published' && (
                    <div className="absolute top-2 left-2 bg-yellow-500 text-white text-xs px-2 py-1">Brouillon</div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-stone-900 line-clamp-1">{book.title}</h3>
                  <p className="text-sm text-stone-600 mt-1">{book.author}</p>
                  <p className="text-xs text-hermes-600 mt-1">{book.collection}</p>
                </div>
                <div className="px-4 pb-4 flex items-center justify-end gap-2">
                  <button onClick={() => togglePublish(book)} className="p-2 text-stone-500 hover:text-hermes-600 transition-colors" title={book.status === 'published' ? 'Dépublier' : 'Publier'}>
                    {book.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <Link to={`/admin/books/${book.id}`} className="p-2 bg-hermes-100 text-hermes-600 hover:bg-hermes-200 rounded transition-colors" title="Modifier">
                    <Pencil className="w-4 h-4" />
                  </Link>
                  <button onClick={() => setDeleteId(book.id)} className="p-2 text-stone-500 hover:text-red-600 transition-colors" title="Supprimer">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {deleteId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white p-6 max-w-sm w-full mx-4 shadow-xl">
              <h3 className="text-lg font-medium text-stone-900 mb-2">Confirmer la suppression</h3>
              <p className="text-stone-600 mb-6">Êtes-vous sûr de vouloir supprimer ce livre ?</p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setDeleteId(null)} className="px-4 py-2 text-stone-600 hover:text-stone-900">Annuler</button>
                <button onClick={() => handleDelete(deleteId)} className="px-4 py-2 bg-red-600 text-white hover:bg-red-700">Supprimer</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminBooksList;
